// music/builder.jsx — AI playlist builder takeover surface.
//
// Flow: brief → /music-api/generate (Claude proposes) → /music-api/resolve
// (match to real Spotify tracks, with art) → editable draft → Play in a room.
//
// Play reuses ha-controller's own dispatch.jsx (amp-listen guard + Sonos
// enqueue) over the panel's live HA connection. "Save to Apple Music" creates a
// real library playlist via the MUT-bridge (apple-save.js + a native minter app,
// see ha-apple-music): Apple's web sign-in is walled, so the Music User Token is
// minted natively and stored server-side. Spotify library writes stay blocked
// (dev-mode), so that path is still play-only.

import * as React from 'react';
import { A_FONT, ALabel, TakeoverShell, BusyDot, fs } from '../direction-a.jsx';
import { RADIUS } from '../theme.js';
import { createDispatch } from './dispatch.jsx';

const { useState, useEffect } = React;

// Play-target Sonos players are built live from the panel's Sonos roster
// (usePanelRooms → speakers), so the dropdown lists every player in the house —
// room speakers AND standalone units (kids' rooms, guest, laundry, portables) —
// not a curated subset. FALLBACK_ROOMS is used only before the live roster has
// loaded; it mirrors the six panel rooms (entities = primarySonosFor()).
const FALLBACK_ROOMS = [
  { id: 'pavilion',       label: 'Pavilion',       entity: 'media_player.pavilion' },
  { id: 'rumpus',         label: 'Rumpus',         entity: 'media_player.rumpus' },
  { id: 'office',         label: 'Office',         entity: 'media_player.office' },
  { id: 'pool',           label: 'Pool',           entity: 'media_player.pool' },
  { id: 'master_bed',     label: 'Master Bed',     entity: 'media_player.master_bed' },
  { id: 'master_ensuite', label: 'Master Ensuite', entity: 'media_player.ensuite' },
];
const ROOM_STORE_KEY = 'ha-music.room';

// Full room labels for the play-target menu. The Sonos roster uses short
// speaker names ('Master', 'Ensuite') that read ambiguously out of the
// grouping UI's context, so room speakers show their room name here instead.
// Standalone speakers (kids' rooms, portables) keep their roster name.
const ROOM_LABELS = {
  office:         'Office',
  pavilion:       'Pavilion',
  rumpus:         'Rumpus',
  master_bed:     'Master Bed',
  master_ensuite: 'Master Ensuite',
  pool:           'Pool',
};

// Map the live speakers roster to play-target options. A room speaker keeps its
// panel roomId as the option id so focusRoomId resolves directly; standalone
// speakers key off their entity id. Falls back to the panel rooms pre-load.
function speakersToRooms(speakers) {
  if (!speakers || speakers.length === 0) return FALLBACK_ROOMS;
  return speakers.map((sp) => ({
    id: sp.roomId || sp.entity,
    label: (sp.roomId && ROOM_LABELS[sp.roomId]) || sp.name || sp.entity,
    entity: sp.entity,
  }));
}

// Resolve the play target against the live roster. Priority: the room the
// builder was opened from (focusRoomId) → last used (localStorage) → first
// available player.
function resolveRoom(rooms, focusRoomId) {
  const ids = new Set(rooms.map((r) => r.id));
  if (focusRoomId && ids.has(focusRoomId)) return focusRoomId;
  try {
    const saved = localStorage.getItem(ROOM_STORE_KEY);
    if (saved && ids.has(saved)) return saved;
  } catch { /* private mode */ }
  return rooms[0]?.id || null;
}

export function BuilderShell({ T, open, viewport, hass, focusRoomId, speakers, onClose }) {
  const [brief, setBrief] = useState('');
  const [length, setLength] = useState(25);
  const [explicit, setExplicit] = useState(true);
  const [busy, setBusy] = useState(false);
  const [genStatus, setGenStatus] = useState('');
  const [error, setError] = useState('');
  const [tracks, setTracks] = useState([]);
  const [misses, setMisses] = useState([]);
  const [name, setName] = useState('');
  const [roomId, setRoomId] = useState(() => resolveRoom(FALLBACK_ROOMS, focusRoomId));
  // Live play-target list. Churns with HA state (speakers is rebuilt per event),
  // but it only feeds the <option>s, so re-renders are harmless.
  const ROOMS = React.useMemo(() => speakersToRooms(speakers), [speakers]);
  const [playing, setPlaying] = useState(false);
  const [playStatus, setPlayStatus] = useState('');
  const [playResult, setPlayResult] = useState('');
  const [candidates, setCandidates] = useState([]); // Claude's {title,artist} — what Apple save re-resolves
  const [appleConnected, setAppleConnected] = useState(null); // null = unchecked
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [saveResult, setSaveResult] = useState('');

  // Resolve the play target when the builder opens or the source room changes.
  // Read speakers via a ref so this fires ONLY on open/focusRoomId — depending
  // on the churning speakers list would re-resolve on every HA state event and
  // stomp a manual dropdown choice.
  const speakersRef = React.useRef(speakers);
  useEffect(() => { speakersRef.current = speakers; }, [speakers]);
  useEffect(() => {
    if (!open) return;
    setRoomId(resolveRoom(speakersToRooms(speakersRef.current), focusRoomId));
  }, [open, focusRoomId]);

  // Reflect whether the native minter has connected Apple Music (MUT in KV).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await freshToken();
        const res = await fetch('/music-api/apple-mut', { headers: { authorization: `Bearer ${token}` } });
        const data = await res.json().catch(() => ({}));
        if (!cancelled) setAppleConnected(res.ok ? !!data.connected : false);
      } catch { if (!cancelled) setAppleConnected(false); }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // HA OAuth access tokens expire (~30 min); refresh before each Function call.
  async function freshToken() {
    const auth = hass?.auth;
    if (!auth) return '';
    try { if (auth.expired) await auth.refreshAccessToken(); } catch { /* use current */ }
    return auth.accessToken || '';
  }

  const chooseRoom = (id) => {
    setRoomId(id);
    try { localStorage.setItem(ROOM_STORE_KEY, id); } catch { /* private mode */ }
  };

  if (!open) return null;
  const isPhone = viewport === 'phone';

  async function generate() {
    if (!brief.trim()) { setError('Describe the playlist you want.'); return; }
    setBusy(true); setError(''); setTracks([]); setMisses([]); setPlayResult(''); setSaveResult(''); setCandidates([]);
    try {
      const token = await freshToken();
      setGenStatus('Asking Claude…');
      const gRes = await fetch('/music-api/generate', {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
        body: JSON.stringify({ brief: brief.trim(), target_length: length, allow_explicit: explicit }),
      });
      const gData = await gRes.json().catch(() => ({}));
      if (!gRes.ok) throw new Error(gData.error || `generate ${gRes.status}`);
      if (gData.suggested_name && !name.trim()) setName(gData.suggested_name);
      setCandidates(gData.candidates || []);

      setGenStatus('Matching tracks…');
      const rRes = await fetch('/music-api/resolve', {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
        body: JSON.stringify({ candidates: gData.candidates, target_length: length, allow_explicit: explicit }),
      });
      const rData = await rRes.json().catch(() => ({}));
      if (!rRes.ok) throw new Error(rData.error || `resolve ${rRes.status}`);
      setTracks(rData.tracks || []);
      setMisses(rData.misses || []);
      if (!rData.tracks?.length) setError('Nothing matched — try a different brief.');
    } catch (e) {
      setError(`Generation failed: ${e.message}`);
    } finally {
      setBusy(false); setGenStatus('');
    }
  }

  function removeTrack(i) {
    setTracks((ts) => ts.filter((_, j) => j !== i));
  }

  // Play the draft on the chosen room's Sonos via ha-controller's dispatch:
  // wakes the amp (Pavilion/Rumpus) then plays the first track and enqueues the
  // rest. Spotify URIs resolve natively on Sonos.
  async function playHere() {
    if (!tracks.length || playing) return;
    const room = ROOMS.find((r) => r.id === roomId);
    if (!room) return;
    if (!hass?.callService) { setError('Not connected to Home Assistant.'); return; }
    setPlaying(true); setError(''); setPlayResult('');
    try {
      const dispatch = createDispatch(hass);
      setPlayStatus(`Starting in ${room.label}…`);
      const coordinator = await dispatch.dispatchToSpeaker({ kind: 'track', id: tracks[0].id }, room.entity);
      if (!coordinator) throw new Error('could not start playback');
      if (tracks.length > 1) {
        setPlayStatus('Queuing the rest…');
        await dispatch.queueTracks(room.entity, tracks.slice(1).map((t) => ({ kind: 'track', id: t.id })));
      }
      setPlayResult(`Playing ${tracks.length} tracks in ${room.label}.`);
    } catch (e) {
      setError(`Play failed: ${e.message}`);
    } finally {
      setPlaying(false); setPlayStatus('');
    }
  }

  // Save the draft to the Apple Music library. Re-resolves Claude's candidates
  // against Apple's catalogue server-side (the on-screen tracks are Spotify IDs)
  // then creates the playlist with the bridged Music User Token.
  async function saveToApple() {
    if (!candidates.length || saving) return;
    setSaving(true); setError(''); setSaveResult('');
    try {
      const token = await freshToken();
      setSaveStatus('Saving to Apple Music…');
      const res = await fetch('/music-api/apple-save', {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
        body: JSON.stringify({ candidates, name: name.trim(), target_length: length, allow_explicit: explicit }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setAppleConnected(true);
        const dropped = (data.misses || []).length;
        setSaveResult(`Saved ${data.playlist?.count ?? ''} tracks to Apple Music as “${data.playlist?.name || name}”.`
          + (dropped ? ` ${dropped} not in Apple’s catalogue.` : ''));
      } else if (data.error === 'apple_not_connected') {
        setAppleConnected(false);
        setError('Apple Music isn’t connected yet — open the MUT minter app to connect.');
      } else if (data.error === 'apple_token_expired') {
        setAppleConnected(false);
        setError('Apple Music sign-in expired — re-mint with the MUT minter app.');
      } else if (data.error === 'no_matches') {
        setError('None of these tracks were on Apple Music.');
      } else {
        throw new Error(data.error || `save ${res.status}`);
      }
    } catch (e) {
      setError(`Apple save failed: ${e.message}`);
    } finally {
      setSaving(false); setSaveStatus('');
    }
  }

  // ── styles (2026-10 panel vocabulary) ────────────────────────────────
  // Fields are quiet fills on a card, no outlines. The one main action
  // per step (Generate, then Play here) is the primary fill; anything in
  // flight lifts out as a key with the busy dot.
  const field = {
    width: '100%', background: T.cardSoft, border: 'none',
    color: T.ink, borderRadius: RADIUS.control, padding: '14px 16px',
    fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
    outline: 'none', boxSizing: 'border-box',
  };
  const btnBase = {
    border: 'none', borderRadius: RADIUS.control, height: 52, padding: '0 20px',
    fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    transition: 'background .15s, color .15s, box-shadow .15s',
  };
  const busyBtn = {
    ...btnBase, background: T.keyFill, boxShadow: T.keyShadow, color: T.ink, cursor: 'default',
  };
  const accentBtn = (inFlight) => (inFlight ? busyBtn : {
    ...btnBase, background: T.primary, color: T.primaryInk, cursor: 'pointer',
  });
  const quietBtn = (inFlight) => (inFlight ? busyBtn : {
    ...btnBase, background: T.cardSoft, color: T.ink, cursor: 'pointer',
  });
  const stepBtn = {
    width: 48, height: 48, borderRadius: '50%', border: 'none', padding: 0,
    background: T.cardSoft, color: T.ink, cursor: 'pointer', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  const card = {
    background: T.card, borderRadius: RADIUS.card, boxShadow: T.cardShadow,
    padding: isPhone ? '18px 18px' : '22px 24px',
  };
  const note = { fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.mute, lineHeight: 1.5 };
  const roomLabel = (ROOMS.find((r) => r.id === roomId) || {}).label;

  return (
    <TakeoverShell T={T} viewport={viewport} eyebrow="Wallace St" title="Playlist Builder" onClose={onClose}>
      <div style={{ maxWidth: 640, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Brief + controls */}
        <div style={card}>
          <ALabel T={T} style={{ marginBottom: 10 }}>What do you want?</ALabel>
          <textarea
            value={brief} onChange={(e) => setBrief(e.target.value)}
            placeholder="e.g. moody Sunday-morning jazz with some newer artists mixed in"
            rows={3} style={{ ...field, resize: 'vertical', minHeight: 96, lineHeight: 1.45 }}/>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button style={stepBtn} onClick={() => setLength((n) => Math.max(10, n - 5))} aria-label="Fewer">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M5 12h14"/></svg>
              </button>
              <span style={{ minWidth: 92, textAlign: 'center', display: 'inline-flex', alignItems: 'baseline', justifyContent: 'center', gap: 6 }}>
                <span style={{
                  fontFamily: A_FONT.serif, fontSize: 30, fontWeight: 500, color: T.ink, lineHeight: 1,
                  fontVariantNumeric: 'lining-nums tabular-nums',
                }}>{length}</span>
                <span style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 14), color: T.inkSoft }}>tracks</span>
              </span>
              <button style={stepBtn} onClick={() => setLength((n) => Math.min(50, n + 5))} aria-label="More">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M5 12h14M12 5v14"/></svg>
              </button>
            </div>
            {/* A toggled chip: the native checkbox stays (visually hidden)
                inside the label for its semantics; the chip draws the state. */}
            <label style={{
              ...btnBase, position: 'relative', height: 48, padding: '0 16px 0 12px', cursor: 'pointer',
              background: explicit ? T.selected : T.cardSoft,
              color: explicit ? T.selectedInk : T.ink,
            }}>
              <input type="checkbox" checked={explicit} onChange={(e) => setExplicit(e.target.checked)}
                style={{ position: 'absolute', opacity: 0, width: 1, height: 1, margin: 0, pointerEvents: 'none' }}/>
              <span aria-hidden style={{
                width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                background: explicit ? T.selectedInk : T.card,
                color: T.selected,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {explicit && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>
                )}
              </span>
              Allow explicit
            </label>
          </div>

          <div style={{ marginTop: 18 }}>
            <button style={{ ...accentBtn(busy), width: '100%' }} disabled={busy} onClick={generate}>
              {busy && <BusyDot T={T} size={14}/>}
              {busy ? (genStatus || 'Working…') : tracks.length ? 'Regenerate' : 'Generate'}
            </button>
          </div>
          {error && (
            <div style={{ marginTop: 12, fontFamily: A_FONT.sans, fontSize: fs(T, 14), fontWeight: 500, color: T.attention, lineHeight: 1.45 }}>{error}</div>
          )}
        </div>

        {/* Draft */}
        {tracks.length > 0 && (
          <div style={card}>
            <ALabel T={T} style={{ marginBottom: 6 }}>Draft · {tracks.length} tracks</ALabel>
            <div>
              {tracks.map((t, i) => (
                <div key={t.id || i} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', minHeight: 66, boxSizing: 'border-box',
                  borderBottom: i < tracks.length - 1 ? `1px solid ${T.hair}` : 'none',
                }}>
                  <div style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.mute, minWidth: 20, textAlign: 'right', fontVariantNumeric: 'lining-nums tabular-nums' }}>{i + 1}</div>
                  {t.art
                    ? <img src={t.art} alt="" width="46" height="46" style={{ borderRadius: 8, flex: 'none', background: T.cardSoft }}/>
                    : <div style={{ width: 46, height: 46, borderRadius: 8, background: T.cardSoft, flex: 'none' }}/>}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500, color: T.ink, lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.title}</div>
                    <div style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.artist}</div>
                    {t.why && (
                      <div style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.mute, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.why}</div>
                    )}
                  </div>
                  <button onClick={() => removeTrack(i)} aria-label="Remove" style={{
                    flex: 'none', width: 44, height: 44, borderRadius: '50%', padding: 0,
                    background: T.cardSoft, border: 'none', color: T.inkSoft, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
                  </button>
                </div>
              ))}
            </div>

            {misses.length > 0 && (
              <div style={{ ...note, marginTop: 10 }}>
                {misses.length} pick{misses.length > 1 ? 's' : ''} weren’t on Spotify and were dropped.
              </div>
            )}
          </div>
        )}

        {/* Name + commit: play to Sonos, or save to the Apple Music library */}
        {tracks.length > 0 && (
          <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Playlist name"
              style={{ ...field, height: 52 }} />

            <div style={{ display: 'flex', gap: 10, flexWrap: isPhone ? 'wrap' : 'nowrap' }}>
              <select value={roomId} onChange={(e) => chooseRoom(e.target.value)}
                style={{ ...field, height: 52, width: isPhone ? '100%' : 200, flex: isPhone ? '1 1 100%' : '0 0 auto', cursor: 'pointer' }}>
                {ROOMS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
              <button style={{ ...accentBtn(playing), flex: 1 }} disabled={playing} onClick={playHere}>
                {playing && <BusyDot T={T} size={14}/>}
                {playing ? (playStatus || 'Playing…') : 'Play here'}
              </button>
            </div>

            <button style={{ ...quietBtn(saving), width: '100%' }} disabled={saving} onClick={saveToApple}>
              {saving && <BusyDot T={T} size={14}/>}
              {saving ? (saveStatus || 'Saving…') : 'Save to Apple Music'}
            </button>

            {playResult && (
              <div style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 14), color: T.ink, fontWeight: 500 }}>{playResult}</div>
            )}
            {saveResult && (
              <div style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 14), color: T.ink, fontWeight: 500 }}>{saveResult}</div>
            )}
            {appleConnected === false && !saveResult && (
              <div style={note}>
                Apple Music isn’t connected — open the MUT minter app to connect, then save.
              </div>
            )}
            <div style={note}>
              Play it on <span style={{ color: T.inkSoft, fontWeight: 500 }}>{roomLabel}</span>'s
              Sonos now, or save it to your Apple Music library. (Spotify library saves stay blocked from web
              apps; Sonos still plays the Spotify matches.)
            </div>
          </div>
        )}

      </div>
    </TakeoverShell>
  );
}
